import { Controller } from "@hotwired/stimulus"

// Global modal controller - placed on <body> tag
// All modals use role="dialog" and aria-modal="true" attributes
// RGAA 4.1 compliant with focus trap implementation
export default class extends Controller {
  connect() {
    // Close modal on Escape key
    this.handleEscape = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.handleEscape)

    // Close modal on background click (for any modal with backdrop)
    this.handleBackdropClick = this.handleBackdropClick.bind(this)
    document.addEventListener("click", this.handleBackdropClick)

    // Un Turbo Stream peut fermer une modal en la remplaçant (ex : succès d'un
    // formulaire modal) sans passer par close() : le verrou de scroll resterait
    // alors actif. On le relâche après chaque rendu de stream si plus aucune
    // modal n'est visible.
    this.releaseScrollLockIfClosed = this.releaseScrollLockIfClosed.bind(this)
    document.addEventListener("turbo:before-stream-render", this.releaseScrollLockIfClosed)

    // Focus trap handler
    this.trapFocus = this.trapFocus.bind(this)

    // Store the element that had focus before modal opened
    this.previouslyFocusedElement = null
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscape)
    document.removeEventListener("click", this.handleBackdropClick)
    document.removeEventListener("turbo:before-stream-render", this.releaseScrollLockIfClosed)
  }

  // Après application du stream (DOM échangé), si aucune modal n'est plus
  // ouverte, lever le verrou de scroll posé à l'ouverture.
  releaseScrollLockIfClosed() {
    requestAnimationFrame(() => {
      const openModals = document.querySelectorAll('[role="dialog"]:not(.hidden)')
      if (openModals.length === 0) {
        document.body.style.overflow = ""
      }
    })
  }

  open(event) {
    event?.preventDefault()

    // Support multiple patterns for backward compatibility
    // 1. New pattern: data-modal-target-param="#modal-id"
    // 2. Legacy pattern: data-modal-id="modal-id"
    const modalSelector = event?.params?.targetParam ||
                         event?.currentTarget?.dataset?.modalTargetParam ||
                         (event?.currentTarget?.dataset?.modalId ? `#${event.currentTarget.dataset.modalId}` : null)

    if (modalSelector) {
      const modal = document.querySelector(modalSelector)
      if (modal) {
        // Store element that triggered modal for focus restoration
        this.previouslyFocusedElement = document.activeElement

        // Move modal to body to escape any containing blocks created by
        // backdrop-filter, transform, etc. on ancestor elements
        // This ensures position: fixed works relative to viewport
        if (modal.parentElement !== document.body) {
          // Store original parent for cleanup
          modal._originalParent = modal.parentElement
          modal._originalNextSibling = modal.nextSibling
          document.body.appendChild(modal)
        }

        // Show modal
        modal.classList.remove("hidden")
        modal.setAttribute("aria-hidden", "false")
        document.body.style.overflow = "hidden"

        // Set up focus trap
        this.setupFocusTrap(modal)

        // Focus first focusable element in modal
        this.focusFirstElement(modal)
      }
    }
  }

  close(event) {
    event?.preventDefault()

    // Find the closest modal element (with role="dialog")
    const modalElement = event?.target?.closest('[role="dialog"]')
    if (modalElement) {
      this.closeModal(modalElement)
    }
  }

  closeModal(modalElement) {
    // Remove focus trap
    modalElement.removeEventListener("keydown", this.trapFocus)

    // Hide modal
    modalElement.classList.add("hidden")
    modalElement.setAttribute("aria-hidden", "true")
    document.body.style.overflow = ""

    // Restore focus to element that opened modal
    if (this.previouslyFocusedElement && this.previouslyFocusedElement.focus) {
      this.previouslyFocusedElement.focus()
      this.previouslyFocusedElement = null
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      // Find all visible modals
      const visibleModals = document.querySelectorAll('[role="dialog"]:not(.hidden)')
      if (visibleModals.length > 0) {
        // Close the last opened modal (topmost)
        const lastModal = visibleModals[visibleModals.length - 1]
        this.closeModal(lastModal)
      }
    }
  }

  handleBackdropClick(event) {
    // Check if click is directly on a modal backdrop (not on modal content)
    const target = event.target
    if (target.hasAttribute("role") && target.getAttribute("role") === "dialog") {
      this.closeModal(target)
    }
  }

  // RGAA 4.1 - Focus Trap Implementation
  setupFocusTrap(modal) {
    // Add keydown listener for Tab key trapping
    modal.addEventListener("keydown", this.trapFocus)
  }

  trapFocus(event) {
    // Only trap Tab key
    if (event.key !== "Tab") return

    const modal = event.currentTarget
    const focusableElements = this.getFocusableElements(modal)

    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    // Shift + Tab on first element -> focus last element
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
      return
    }

    // Tab on last element -> focus first element
    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
      return
    }
  }

  getFocusableElements(modal) {
    // RGAA 4.1 - Liste complète des éléments focusables
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([type="hidden"]):not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(', ')

    const elements = Array.from(modal.querySelectorAll(focusableSelectors))

    // Filter out elements that are not visible
    return elements.filter(element => {
      return element.offsetWidth > 0 &&
             element.offsetHeight > 0 &&
             !element.hasAttribute('hidden') &&
             window.getComputedStyle(element).visibility !== 'hidden'
    })
  }

  focusFirstElement(modal) {
    const focusableElements = this.getFocusableElements(modal)

    if (focusableElements.length > 0) {
      // Focus first element with slight delay to ensure modal is visible
      setTimeout(() => {
        focusableElements[0].focus()
      }, 100)
    }
  }
}
